/**
 * A hook that tracks whether an element is in the viewport.
 * Used to trigger animations or lazy loading when elements become visible.
 *
 * @param threshold - The amount of the element that needs to be visible (0-1)
 * @param rootMargin - Margin around the root element (CSS format like '10px 20px')
 * @param once - If true, the hook stops observing after the element becomes visible
 * @returns Object containing ref to attach to target element and isInView state
 */
import { useState, useEffect, useRef } from 'react';

interface InViewOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useInView(
  thresholdOrOptions: number | InViewOptions = 0.1,
  rootMargin?: string,
  once?: boolean
) {
  // Handle both function signatures (object options or separate parameters)
  const options =
    typeof thresholdOrOptions === 'object'
      ? thresholdOrOptions
      : { threshold: thresholdOrOptions, rootMargin, once };

  const threshold = options.threshold ?? 0.1;
  const margin = options.rootMargin ?? '0px';
  const observeOnce = options.once ?? true;

  const ref = useRef<HTMLElement | null>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          if (observeOnce) observer.unobserve(element);
        } else if (!observeOnce) {
          setIsInView(false); // Reset for re-triggering
        }
      },
      { threshold, rootMargin: margin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      // Disconnect is a more thorough cleanup
      observer.disconnect();
    };
  }, [threshold, margin, observeOnce]);

  return { ref, isInView };
}
